import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Observable, Subject, forkJoin, of, throwError } from 'rxjs';
import { IAssetClass } from 'src/app/core/models/i-asset-class';
import { IEtf } from 'src/app/core/models/i-etf';
import { IFund } from 'src/app/core/models/i-fund';
import { IInvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { IManager } from 'src/app/core/models/i-manager';
import { IPrivateMarketsFund } from 'src/app/core/models/i-private-markets-fund';
import { ISector } from 'src/app/core/models/i-sector';
import { IShare } from 'src/app/core/models/i-share';
import { ManagerService } from '../services/manager.service';
import { Constructor } from '@angular/cdk/table';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { RELATED_MANAGER_FORM_PATHS } from 'src/app/shared-form-sections/components/related-manager-item/constants/form-paths';
import { IManagerResponse } from 'src/app/core/models/i-manager-response';
import { compareArrays } from '../utils/array-utils';
import { RelatedManager } from 'src/app/core/models/i-related-manager';

type WithRelatedDataFormSectionType = {
  _initRelatedData(
    managers: IManager[],
    funds: IFund[],
    sectors: ISector[],
    assetClasses: IAssetClass[],
    etfs: IEtf[],
    investmentTrusts: IInvestmentTrust[],
    shares: IShare[],
    privateMarketsFunds?: IPrivateMarketsFund[]
  ): void;
  _initRelatedDataValueChanges(
    getPreviousSelectedManagers: () => IManager[] | null,
    getPreviousSelectedFunds: () => IFund[] | null,
    getPreviousSelectedSectors: () => ISector[] | null,
    getPreviousSelectedAssetClasses: () => IAssetClass[] | null,
    getPreviousSelectedEtfs: () => IEtf[] | null,
    getPreviousSelectedInvestmentTrusts: () => IInvestmentTrust[] | null,
    getPreviousSelectedShares: () => IShare[] | null,
    getPreviousSelectedPrivateMarketsFunds?: () => IPrivateMarketsFund[] | null
  ): void;
  _buildRelatedManagers(): IManager[];
  _buildRelatedFunds(): IFund[];
  _buildRelatedSectors(): ISector[];
  _buildRelatedAssetClasses(): IAssetClass[];
  _buildRelatedEtfs(): IEtf[];
  _buildRelatedInvestmentTrusts(): IInvestmentTrust[];
  _buildRelatedShares(): IShare[];
  _buildRelatedPrivateMarketsFunds(): IPrivateMarketsFund[];
  get relatedManagers(): IManager[];
  get relatedFunds(): IFund[];
  get relatedSectors(): ISector[];
  get relatedAssetClasses(): IAssetClass[];
  get relatedEtfs(): IEtf[];
  get relatedInvestmentTrusts(): IInvestmentTrust[];
  get relatedShares(): IShare[];
  get relatedPrivateMarketsFunds(): IPrivateMarketsFund[];
};

type RelatedDataFormSectionRequirementsBase = {
  form: FormGroup;
  FORM_PATHS: {
    RELATED_MANAGERS: () => string;
    RELATED_FUNDS: () => string;
    RELATED_SECTORS: () => string;
    RELATED_ASSET_CLASSES: () => string;
    RELATED_ETFS: () => string;
    RELATED_INVESTMENT_TRUSTS: () => string;
    RELATED_SHARES: () => string;
    RELATED_PRIVATE_MARKETS_FUNDS?: () => string;
  };
  destroy$: Subject<boolean>;
  managerService: ManagerService;
  formBuilder: FormBuilder;
};

export function WithRelatedDataFormSection<
  TBase extends Constructor<RelatedDataFormSectionRequirementsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithRelatedDataFormSectionType;
  prototype: WithRelatedDataFormSectionType;
} & TBase {
  return class WithRelatedDataFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initRelatedData(
      managers: IManager[],
      funds: IFund[],
      sectors: ISector[],
      assetClasses: IAssetClass[],
      etfs: IEtf[],
      investmentTrusts: IInvestmentTrust[],
      shares: IShare[],
      privateMarketsFunds?: IPrivateMarketsFund[]
    ): void {
      if (managers) {
        const managersControl = this.form.get(
          this.FORM_PATHS.RELATED_MANAGERS()
        ) as FormArray<FormGroup>;

        forkJoin(
          managers.map((manager: IManager) =>
            this._fetchSectorsForManager(manager)
          )
        ).subscribe((updatedManagers: IManager[]) => {
          updatedManagers.forEach((manager) => {
            managersControl.push(
              this.formBuilder.group({
                [RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()]: manager,
                [RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()]:
                  manager.selectedSector,
              }),
              { emitEvent: false }
            );
          });
          managersControl.markAsPristine();
        });
      }

      if (funds) {
        this.initializeFormArray(funds, this.FORM_PATHS.RELATED_FUNDS());
      }

      if (sectors) {
        this.initializeFormArray(sectors, this.FORM_PATHS.RELATED_SECTORS());
      }

      if (assetClasses) {
        this.initializeFormArray(
          assetClasses,
          this.FORM_PATHS.RELATED_ASSET_CLASSES()
        );
      }

      if (etfs) {
        this.initializeFormArray(etfs, this.FORM_PATHS.RELATED_ETFS());
      }

      if (investmentTrusts) {
        this.initializeFormArray(
          investmentTrusts,
          this.FORM_PATHS.RELATED_INVESTMENT_TRUSTS()
        );
      }

      if (shares) {
        this.initializeFormArray(shares, this.FORM_PATHS.RELATED_SHARES());
      }

      if (privateMarketsFunds && this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS) {
        this.initializeFormArray(
          privateMarketsFunds,
          this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS()
        );
      }
    }

    private initializeFormArray(array: any[], formPath: string) {
      const control = this.form.get(formPath);
      array.forEach((i) => {
        (control as FormArray).push(new FormControl(i));
      });
    }

    private _fetchSectorsForManager(manager: IManager): Observable<IManager> {
      return this.managerService.getManagerById$(manager.id).pipe(
        mergeMap((managerResponse: IManagerResponse | null) => {
          if (!managerResponse) {
            return throwError(() => new Error('Manager response is null or undefined'));
          }

          manager.sectors = managerResponse.sectors ?? [];
          manager.rating = managerResponse.rating ?? '';
          return of(manager);
        }),
        catchError((error) => {
          return of(manager);
      })
      );
    }

    _initRelatedDataValueChanges(
      getPreviousSelectedManagers: () => IManager[] | null,
      getPreviousSelectedFunds: () => IFund[] | null,
      getPreviousSelectedSectors: () => ISector[] | null,
      getPreviousSelectedAssetClasses: () => IAssetClass[] | null,
      getPreviousSelectedEtfs: () => IEtf[] | null,
      getPreviousSelectedInvestmentTrusts: () => IInvestmentTrust[] | null,
      getPreviousSelectedShares: () => IShare[] | null,
      getPreviousSelectedPrivateMarketsFunds?: () => IPrivateMarketsFund[] | null
    ): void {
      this.initValueChangesTracking(
        getPreviousSelectedManagers,
        this.FORM_PATHS.RELATED_MANAGERS()
      );
      this.initValueChangesTracking(
        getPreviousSelectedFunds,
        this.FORM_PATHS.RELATED_FUNDS()
      );
      this.initValueChangesTracking(
        getPreviousSelectedSectors,
        this.FORM_PATHS.RELATED_SECTORS()
      );
      this.initValueChangesTracking(
        getPreviousSelectedAssetClasses,
        this.FORM_PATHS.RELATED_ASSET_CLASSES()
      );
      this.initValueChangesTracking(
        getPreviousSelectedEtfs,
        this.FORM_PATHS.RELATED_ETFS()
      );
      this.initValueChangesTracking(
        getPreviousSelectedInvestmentTrusts,
        this.FORM_PATHS.RELATED_INVESTMENT_TRUSTS()
      );
      this.initValueChangesTracking(
        getPreviousSelectedShares,
        this.FORM_PATHS.RELATED_SHARES()
      );

      if (
        getPreviousSelectedPrivateMarketsFunds &&
        this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS
      ) {
        this.initValueChangesTracking(
          getPreviousSelectedPrivateMarketsFunds,
          this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS()
        );
      }
    }

    private initValueChangesTracking(
      getPreviousValue: () => any[] | null,
      formPath: string
    ) {
      this.form
        .get(formPath)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousValue())) {
              this.form.get(formPath).markAsPristine();
            } else {
              this.form.get(formPath).markAsDirty();
            }
          },
        });
    }

    _buildRelatedManagers(): IManager[] {
      let relatedManagers = (
        this.form.get(this.FORM_PATHS.RELATED_MANAGERS()) as FormArray
      ).controls.map((formGroup) => {
        let selectedSectorControl = formGroup.get(
          RELATED_MANAGER_FORM_PATHS.SELECTED_SECTOR()
        );
        let managerControl = formGroup.get(
          RELATED_MANAGER_FORM_PATHS.SELECTED_MANAGER()
        );
        return new RelatedManager(
          managerControl.value,
          selectedSectorControl.value
        );
      });
      return this.managerService.mapRelatedManagersToManager(relatedManagers);
    }

    _buildRelatedFunds(): IFund[] {
      return this.form.get(this.FORM_PATHS.RELATED_FUNDS()).value;
    }

    _buildRelatedSectors(): ISector[] {
      return this.form.get(this.FORM_PATHS.RELATED_SECTORS()).value;
    }

    _buildRelatedAssetClasses(): IAssetClass[] {
      return this.form.get(this.FORM_PATHS.RELATED_ASSET_CLASSES()).value;
    }

    _buildRelatedEtfs(): IEtf[] {
      return this.form.get(this.FORM_PATHS.RELATED_ETFS()).value;
    }

    _buildRelatedInvestmentTrusts(): IInvestmentTrust[] {
      return this.form.get(this.FORM_PATHS.RELATED_INVESTMENT_TRUSTS()).value;
    }

    _buildRelatedShares(): IShare[] {
      return this.form.get(this.FORM_PATHS.RELATED_SHARES()).value;
    }

    _buildRelatedPrivateMarketsFunds(): IPrivateMarketsFund[] {
      if (!this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS) {
        return [];
      }
      return (
        this.form.get(this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS())?.value ??
        []
      );
    }

    get relatedManagers(): IManager[] {
      return this.form.get(this.FORM_PATHS.RELATED_MANAGERS()).value;
    }

    get relatedFunds(): IFund[] {
      return this.form.get(this.FORM_PATHS.RELATED_FUNDS()).value;
    }

    get relatedSectors(): ISector[] {
      return this.form.get(this.FORM_PATHS.RELATED_SECTORS()).value;
    }

    get relatedAssetClasses(): IAssetClass[] {
      return this.form.get(this.FORM_PATHS.RELATED_ASSET_CLASSES()).value;
    }

    get relatedEtfs(): IEtf[] {
      return this.form.get(this.FORM_PATHS.RELATED_ETFS()).value;
    }

    get relatedInvestmentTrusts(): IInvestmentTrust[] {
      return this.form.get(this.FORM_PATHS.RELATED_INVESTMENT_TRUSTS()).value;
    }

    get relatedShares(): IShare[] {
      return this.form.get(this.FORM_PATHS.RELATED_SHARES()).value;
    }

    get relatedPrivateMarketsFunds(): IPrivateMarketsFund[] {
      if (!this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS) {
        return [];
      }
      return (
        this.form.get(this.FORM_PATHS.RELATED_PRIVATE_MARKETS_FUNDS())?.value ??
        []
      );
    }
  };
}
